/**
 * Test layouts: interface layouts shown only to admins and super admins while they are validated
 * (docs/test-layouts.md). Each one is a stylesheet loaded on demand, so nobody else downloads it, plus
 * the shell pieces its capabilities turn on. The five public layouts stay in AppShell's LAYOUT_OPTIONS.
 */

import type { ComponentType, ReactNode } from 'react';
import type { RecordSidePanelProps } from '../../components/records/RecordSidePanel';
import type { RecordStatusBarProps } from '../../components/records/RecordStatusBar';
import type { RecordStageLineProps } from '../../components/records/RecordStageLine';
import type { LibraryPreviewPanelProps } from '../../components/library/LibraryPreviewPanel';
import type { TodayGlanceProps } from '../../components/home/TodayGlance';
import type { RecordJournalProps } from '../../components/records/RecordJournal';
import type { RecordContentsProps } from '../../components/records/RecordContents';
import type { RouteBreadcrumbProps, RouteTitleProps } from '../../components/shell/RouteBreadcrumb';

export type TestLayoutId = 'focus' | 'glass' | 'field' | 'studio';

/** A bottom bar slot: Home, a primary nav key, the palette ('search') or the phone menu ('menu' / 'more' / 'routes'). */
export type BottomNavKey = 'home' | 'events' | 'schedule' | 'service' | 'search' | 'menu' | 'more' | 'routes';

export interface TestLayoutCapabilities {
  /** Nav links carry an icon and a label span (AdaptivePrimaryNav, the phone menu). */
  navIcons: boolean;
  /** The top bar becomes a side rail from `railMinWidth`, so no bar sits above the page. */
  sideRail: boolean;
  railMinWidth: number;
  /** Ctrl/⌘+K opens the command palette. */
  commandPalette: boolean;
  /** A strip above the page holds the palette's search trigger where the rail has no room for one. */
  commandStrip: boolean;
  /** The palette's search trigger sits in the top bar, beside the bell. */
  commandField: boolean;
  /**
   * A bar fixed to the bottom holds these destinations instead of the ☰ menu button. The base styles show
   * it below 768px; a layout's stylesheet may show it wider (Glass: up to the 1120px mobile breakpoint).
   */
  bottomNav: ReadonlyArray<BottomNavKey> | null;
  /**
   * The ☰ button shows this word beside its icon and takes it as its name, and the phone menu takes it as
   * its title (Field: "Routes", Studio: "Contents").
   */
  menuLabel: string | null;
  /** The nav renders in the bar from this width even below the 1120px mobile breakpoint, without a rail. */
  navMinWidth: number | null;
  /** The nav keeps every link and scrolls sideways when they don't fit, instead of moving some into More. */
  navScrolls: boolean;
}

/**
 * Page content a test layout adds to pages (docs/test-layouts.md, "Page features"). Named by what it does,
 * never by layout; pages read the applied layout's flags through `useShellLayout()`.
 */
export interface PageFeatures {
  /** The Events list previews the hovered or focused row in a side panel. */
  listPreview: boolean;
  /** The Event record's properties and actions sit in a side panel. */
  recordSidePanel: boolean;
  /** Home shows today at a glance and the quick actions with their keys. */
  homeGlance: boolean;
  /** A breadcrumb above the page and the numbered page title in the phone bar. */
  breadcrumb: boolean;
  /** The Event record's Basic Info opens with its facts and its dates as a journal. */
  recordJournal: boolean;
  /** The Event record's approval card is a permit, stamped AWAITING or APPROVED. */
  recordPermit: boolean;
  /** The stage is written as one line instead of drawn as a stepper. */
  stageLine: boolean;
  /** A kicker line and a one-sentence standfirst above record and library titles. */
  standfirst: boolean;
  /** The Event record's tabs move into a margin column as a numbered Contents list. */
  recordTabsInMargin: boolean;
  /** The Event record's status, totals and actions sit in a bar along the bottom instead of a side panel. */
  recordStatusBar: boolean;
  /** The Event record's tabs show their fields as label and value rows, each edited in place. */
  recordPropertyRows: boolean;
}

export const NO_PAGE_FEATURES: PageFeatures = Object.freeze({
  listPreview: false,
  recordSidePanel: false,
  homeGlance: false,
  breadcrumb: false,
  recordJournal: false,
  recordPermit: false,
  stageLine: false,
  standfirst: false,
  recordTabsInMargin: false,
  recordStatusBar: false,
  recordPropertyRows: false,
});

/**
 * A test layout's page kit: the components that render its page features (ui/layouts/<id>.kit.tsx). A page
 * renders a piece only when its feature is on and the kit has it.
 */
export interface PageKit {
  layout: TestLayoutId;
  RecordSidePanel?: ComponentType<RecordSidePanelProps>;
  RecordStatusBar?: ComponentType<RecordStatusBarProps>;
  RecordStageLine?: ComponentType<RecordStageLineProps>;
  RecordKicker?: ComponentType<RecordKickerSlotProps>;
  RecordStandfirst?: ComponentType<{ children: ReactNode }>;
  LibraryPreviewPanel?: ComponentType<LibraryPreviewPanelProps>;
  TodayGlance?: ComponentType<TodayGlanceProps>;
  RecordJournal?: ComponentType<RecordJournalProps>;
  RouteBreadcrumb?: ComponentType<RouteBreadcrumbProps>;
  RouteTitle?: ComponentType<RouteTitleProps>;
  RecordContents?: ComponentType<RecordContentsProps>;
}

/** What a record gives its kicker; each layout words it ("Event 0912130", "Entry 0912130 · Jul 25–27"). */
export interface RecordKickerSlotProps {
  code: string | null;
  /** The record's stage as the page names it ("Quote"). */
  stage: string | null;
  dates: string | null;
  place: string | null;
}

export interface TestLayoutOption {
  id: TestLayoutId;
  label: string;
  description: string;
  capabilities: TestLayoutCapabilities;
  pageFeatures: PageFeatures;
  loadStyles: () => Promise<unknown>;
  /** Loaded with the stylesheet; the layout applies only once both have arrived. */
  loadPageKit: () => Promise<PageKit>;
}

export const TEST_LAYOUT_OPTIONS: readonly TestLayoutOption[] = [
  {
    id: 'focus',
    label: 'Focus',
    description: 'Icon rail, keyboard first',
    capabilities: {
      navIcons: true,
      sideRail: true,
      railMinWidth: 768,
      commandPalette: true,
      commandStrip: true,
      commandField: false,
      bottomNav: ['home', 'events', 'schedule', 'search', 'menu'],
      menuLabel: null,
      navMinWidth: null,
      navScrolls: false,
    },
    pageFeatures: { ...NO_PAGE_FEATURES, listPreview: true, homeGlance: true, stageLine: true, standfirst: true, recordStatusBar: true, recordPropertyRows: true },
    loadStyles: () => Promise.all([import('./focus.css'), import('./shared.css')]),
    loadPageKit: () => import('./focus.kit').then((module) => module.default),
  },
  {
    id: 'glass',
    label: 'Glass',
    description: 'Floating glass dock, depth and light',
    capabilities: {
      navIcons: true,
      sideRail: false,
      railMinWidth: 0,
      commandPalette: true,
      commandStrip: false,
      commandField: true,
      bottomNav: ['home', 'events', 'schedule', 'service', 'more'],
      menuLabel: null,
      navMinWidth: null,
      navScrolls: false,
    },
    pageFeatures: NO_PAGE_FEATURES,
    loadStyles: () => Promise.all([import('./glass.css'), import('./shared.css')]),
    loadPageKit: () => import('./glass.kit').then((module) => module.default),
  },
  {
    id: 'field',
    label: 'Field',
    description: 'Numbered route rail, field journal',
    capabilities: {
      navIcons: true,
      sideRail: true,
      railMinWidth: 1024,
      commandPalette: true,
      commandStrip: false,
      commandField: false,
      bottomNav: ['events', 'service', 'schedule', 'routes'],
      menuLabel: 'Routes',
      navMinWidth: null,
      navScrolls: false,
    },
    pageFeatures: { ...NO_PAGE_FEATURES, breadcrumb: true, recordPermit: true, stageLine: true, standfirst: true },
    loadStyles: () => Promise.all([import('./field.css'), import('./shared.css')]),
    loadPageKit: () => import('./field.kit').then((module) => module.default),
  },
  {
    id: 'studio',
    label: 'Studio',
    description: 'Editorial masthead, paper and rules',
    capabilities: {
      navIcons: false,
      sideRail: false,
      railMinWidth: 0,
      commandPalette: true,
      commandStrip: false,
      commandField: true,
      bottomNav: null,
      menuLabel: 'Contents',
      navMinWidth: 768,
      navScrolls: true,
    },
    pageFeatures: { ...NO_PAGE_FEATURES, recordJournal: true, stageLine: true, standfirst: true, recordTabsInMargin: true },
    loadStyles: () => Promise.all([import('./studio.css'), import('./shared.css')]),
    loadPageKit: () => import('./studio.kit').then((module) => module.default),
  },
];

const TEST_LAYOUTS = new Map<string, TestLayoutOption>(TEST_LAYOUT_OPTIONS.map((option) => [option.id, option]));

export function isTestLayoutId(value: string | null | undefined): value is TestLayoutId {
  return !!value && TEST_LAYOUTS.has(value);
}

export function getTestLayout(id: string | null | undefined): TestLayoutOption | null {
  return (id && TEST_LAYOUTS.get(id)) || null;
}
